import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useColorMode } from '@chakra-ui/react';
import invariant from 'invariant';

import { useShortcodeSettings } from '.';
import { defaultCalendarSettings } from '../../lib/constants';
import { normalizeSettings } from '../../lib/normalizeSettings';

import type { ICalendarPlusSettings } from '../../lib/types';

interface CalendarSettingsContextType {
	settings: ICalendarPlusSettings;
}

const CalendarSettingsContext = createContext<CalendarSettingsContextType | undefined>(undefined);

export const CalendarSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { colorMode } = useColorMode();
	const [settings, setSettings] = useState<ICalendarPlusSettings>(defaultCalendarSettings);
	const { settings: shortcodeSettings } = useShortcodeSettings();

	useEffect(() => {
		if (settings && shortcodeSettings) {
			const updatedSettings = { ...settings };
			let updateFlag = false;
			['searchFilter', 'locationFilter', 'categoryFilter', 'venueFilter', 'tagFilter'].forEach((filter) => {
				if (shortcodeSettings[filter] !== undefined) {
					updatedSettings[`enable${filter.charAt(0).toUpperCase() + filter.slice(1)}`] =
						shortcodeSettings[filter];
					updateFlag = true;
				}
			});
			if (updateFlag) setSettings({ ...settings, ...updatedSettings });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shortcodeSettings]);

	useEffect(() => {
		if (typeof window.calendarPlusSettings !== 'undefined') {
			const normalizedSettings = normalizeSettings(window.calendarPlusSettings, defaultCalendarSettings);
			setSettings(normalizedSettings);
		}
	}, []);

	useEffect(() => {
		if (settings.styles[colorMode]?.calendarTodayCellBackgroundColor) {
			document.documentElement.style.setProperty(
				'--today-background-color',
				settings.styles[colorMode].calendarTodayCellBackgroundColor
			);
		}
		if (settings.styles[colorMode]?.calendarBordersColor) {
			document.documentElement.style.setProperty(
				'--calendar-borders-color',
				settings.styles[colorMode].calendarBordersColor
			);
		}
	}, [settings, colorMode]);

	const { timeFormat, monthFormat, dateFormat } = useMemo(() => {
		return {
			timeFormat: settings.timeFormat === 'custom' ? settings.customTimeFormat : settings.timeFormat,
			monthFormat: settings.monthFormat === 'custom' ? settings.customMonthFormat : settings.monthFormat,
			dateFormat: settings.dateFormat === 'custom' ? settings.customDateFormat : settings.dateFormat,
		};
	}, [settings]);

	const contextValue = useMemo(() => {
		return {
			settings: {
				...settings,
				formats: {
					timeFormat,
					monthFormat,
					dateFormat,
				},
			},
		};
	}, [settings, timeFormat, monthFormat, dateFormat]);

	return <CalendarSettingsContext.Provider value={contextValue}>{children}</CalendarSettingsContext.Provider>;
};

export const useCalendarSettings = (): CalendarSettingsContextType => {
	const context = useContext(CalendarSettingsContext);

	invariant(context, 'useCalendarSettings must be used within a CalendarSettingsProvider');

	return context;
};
